import { useSharedCalendarSetting } from '@/api/useSharedCalendar';
import { useGetSharedCalendar } from '@/api/useSharedCalendar';
import { useEffect, useMemo } from 'react';

type SharedCalendarConfigProps = {
  config?: {
    name?: string;
    label?: string;
    [key: string]: any;
  };
  isPlan?: boolean;
};

const DefaultCalendarOption = {
  label: "Organizer's calendar",
  value: 'organizer_calendar',
  calendarId: 'organizer_calendar',
  userId: 0,
  isPrimary: false,
  isDefault: false,
};

/**
 * Calendar field for interview-style flows. Gated by
 * `GET /shared_calendars/show_setting` — when `shared_calendar_enabled` is false,
 * the Calendar field is omitted entirely (no trigger_fetch).
 */
export const useSharedCalendarConfig = ({
  config,
  isPlan = true,
}: SharedCalendarConfigProps = {}) => {
  const { name = 'calendar', ...overRides } = config ?? {};

  const {
    data: sharedCalendarEnabled,
    isPending: isSharedCalendarSettingLoading,
    isError: isSharedCalendarSettingError,
  } = useSharedCalendarSetting();

  const showSharedCalendarField =
    !isSharedCalendarSettingLoading &&
    !isSharedCalendarSettingError &&
    sharedCalendarEnabled === true;

  const { calendars, isLoading, fetchSharedCalendars } = useGetSharedCalendar({
    featureEnabled: showSharedCalendarField,
  });

  useEffect(() => {
    if (showSharedCalendarField) {
      fetchSharedCalendars();
    }
  }, [fetchSharedCalendars, showSharedCalendarField]);

  const calendarSelectOptions = useMemo(() => {
    const list = calendars ?? [];
    const filtered = isPlan
      ? list.filter((cal) => !cal?.calendarId?.includes?.('@kula.ai'))
      : list;
    const withDefault = isPlan
      ? [DefaultCalendarOption, ...filtered]
      : [...filtered];
    return withDefault;
  }, [calendars, isPlan]);

  const calendarConfig = useMemo(() => {
    const base = {
      type: 'select' as const,
      label: 'Calendar',
      name,
      isLoading,
      options: calendarSelectOptions,
      ...overRides,
      customOnChange: (selectedOption: unknown, formik?: any) => {
        if (!isPlan) {
          formik?.setFieldValue?.('calendarUiViewMode', 'info');
        }
        overRides.customOnChange?.(selectedOption, formik);
      },
    };

    if (isPlan) {
      return base;
    }

    return {
      ...base,
      isConditional: true,
      conditionConfig: { field: 'calendarUiViewMode', value: 'calendar' },
    };
  }, [name, isLoading, calendarSelectOptions, overRides, isPlan]);

  const calendarInfo = useMemo(
    () => ({
      isConditional: true,
      conditionConfig: { field: 'calendarUiViewMode', value: 'info' },
      type: 'custom' as const,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
      Field: ({ formik }: { formik: any }) => (
        //todo: build and add calendar info here
        <></>
      ),
    }),
    []
  );

  const formConfig = useMemo(() => {
    if (!showSharedCalendarField) {
      return [];
    }
    return isPlan ? [calendarConfig] : [calendarInfo, calendarConfig];
  }, [showSharedCalendarField, isPlan, calendarInfo, calendarConfig]);

  return {
    formConfig,
    /** Spread into Formik initial values when using `isPlan: false`. */
    sharedCalendarInitialValues: isPlan ? {} : { calendarUiViewMode: 'info' },
    /** True while `show_setting` is loading — calendar field not yet decided. */
    isSharedCalendarSettingLoading,
    /** After load: feature flag allows shared calendar UI + fetch. */
    showSharedCalendarField,
  };
};
